/**
 * Canonical tile definitions for the .pocketgolf course format.
 *
 * This file is the map editor's self-contained copy of the tile table. It used to be
 * imported from the React Native app (`../src/constants`), which coupled the editor's
 * build to the app and forced a `react-native` module alias just to satisfy an unused
 * `Dimensions` import. The editor now owns its own copy.
 *
 * `mapChar` is the on-disk representation: each grid row in a .pocketgolf file is a
 * string of these characters, as long as the hole is wide (see grid.js). Changing a
 * `mapChar` breaks every existing course file, so don't. The physics fields are not used by the editor itself, but are
 * kept here as documentation of what each tile does in game.
 *
 * The Swift game (PocketGolf2) has its own copy in Sources/PocketGolfCore/TileType.swift.
 * The two must agree on `mapChar`; keep them in sync.
 */

/** Insertion order determines the order of the editor's tile palette. */
export const TILES = {
	TEE: {
		mapChar: 'X',
		mapTitle: 'TeeBox',
		editorIcon: '🏌️‍♂️',
		bounce: 1.0,
		friction: 0,
		accuracy: 1,
		color: '#45CD39',
		skillSpeedMultiplier: 1.0,
	},
	FAIRWAY: {
		mapChar: 'F',
		mapTitle: 'Fairway',
		editorIcon: null,
		bounce: 1.0,
		friction: 0,
		accuracy: 0.95,
		color: '#45CD39',
		skillSpeedMultiplier: 1.0,
	},
	ROUGH: {
		mapChar: 'R',
		mapTitle: 'Rough',
		editorIcon: null,
		bounce: 0.8,
		friction: 0.3,
		accuracy: 0.75,
		color: '#009502',
		skillSpeedMultiplier: 1.15,
	},
	WATER: {
		mapChar: 'W',
		mapTitle: 'Water',
		editorIcon: null,
		bounce: 0,
		friction: 0,
		accuracy: 1,
		color: '#1E90FF',
		skillSpeedMultiplier: 1.0,
	},
	SAND: {
		mapChar: 'S',
		mapTitle: 'Sand',
		editorIcon: null,
		bounce: 0.5,
		friction: 0.45,
		accuracy: 0.7,
		color: '#F4A460',
		skillSpeedMultiplier: 1.4,
	},
	GREEN: {
		mapChar: 'G',
		mapTitle: 'Green',
		editorIcon: null,
		bounce: 1.0,
		friction: -0.1,
		accuracy: 0.95,
		color: '#20C237',
		skillSpeedMultiplier: 0.85,
	},
	HOLE: {
		mapChar: '0',
		mapTitle: 'Hole',
		editorIcon: '⛳️',
		bounce: 0,
		friction: 1,
		accuracy: 1,
		color: '#20C237',
		skillSpeedMultiplier: 0.85,
	},
	TREE: {
		mapChar: 'T',
		mapTitle: 'Tree',
		editorIcon: '🌲',
		bounce: 0.7,
		friction: 0.1,
		accuracy: 0.75,
		color: '#4ba14b',
		hitChance: 0.07,
		skillSpeedMultiplier: 1.1,
	},
	DENSE_TREE: {
		mapChar: 'Y',
		mapTitle: 'DenseTree',
		editorIcon: '🌳',
		bounce: 0.7,
		friction: 0.3,
		accuracy: 0.5,
		color: '#009502',
		hitChance: 0.5,
		skillSpeedMultiplier: 1.2,
	},
	PATHWAY: {
		mapChar: 'P',
		mapTitle: 'Pathway',
		editorIcon: '🛤️',
		bounce: 1.8,
		friction: 0.4,
		accuracy: 1,
		color: '#2f2f2f',
	},
	ROCK: {
		mapChar: 'K',
		mapTitle: 'Rock',
		editorIcon: '🪨',
		bounce: 4,
		friction: 0.8,
		accuracy: 0.6,
		color: '#7C7C7C',
		skillSpeedMultiplier: 1.0,
	},
	BUSH: {
		mapChar: 'B',
		mapTitle: 'Bush',
		editorIcon: '🌿',
		bounce: 0.5,
		friction: 0.8,
		accuracy: 0.6,
		lostChance: 0.3,
		color: '#0f5e17',
		skillSpeedMultiplier: 1.0,
	},
	OUT_OF_BOUNDS: {
		mapChar: 'O',
		mapTitle: 'OutOfBounds',
		editorIcon: null,
		bounce: 0,
		friction: 1,
		accuracy: 0.6,
		color: '#555555',
		skillSpeedMultiplier: 1.0,
	},
};

export default TILES;
